import * as fs from 'fs-extra';
import * as path from 'path';
import { BackupManager as IBackupManager, BackupData, BackupError } from './types';

export class BackupManager implements IBackupManager {
  private backupDir = '.dezzy/backups';

  async create(projectPath: string, metadata: any = {}): Promise<BackupData> {
    const timestamp = new Date();
    const backupId = this.generateBackupId(timestamp);
    const backupPath = path.join(projectPath, this.backupDir, backupId);

    try {
      await fs.ensureDir(backupPath);

      // Collect all project files (excluding node_modules and other irrelevant directories)
      const files = await this.collectProjectFiles(projectPath);
      
      // Save files to backup directory
      for (const file of files) {
        const backupFilePath = path.join(backupPath, file.path);
        await fs.ensureDir(path.dirname(backupFilePath));
        await fs.writeFile(backupFilePath, file.content);
      }

      // Create backup metadata
      const backup: BackupData = {
        id: backupId,
        timestamp,
        projectPath,
        backupPath,
        version: await this.detectProjectVersion(projectPath),
        files,
        metadata
      };

      // Save backup metadata
      const metadataPath = path.join(backupPath, 'backup.json');
      await fs.writeJson(metadataPath, backup, { spaces: 2 });

      return backup;
    } catch (error) {
      // Cleanup failed backup
      if (await fs.pathExists(backupPath)) {
        await fs.remove(backupPath);
      }
      
      throw new BackupError(
        `Failed to create backup: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'create'
      );
    }
  }

  async restore(backup: BackupData, targetPath: string): Promise<void> {
    try {
      // Verify backup exists
      if (!(await fs.pathExists(backup.backupPath))) {
        throw new Error(`Backup path does not exist: ${backup.backupPath}`);
      }

      // Clear target directory (except node_modules and backups)
      await this.clearTargetDirectory(targetPath);

      // Restore files from backup
      for (const file of backup.files) {
        const targetFilePath = path.join(targetPath, file.path);
        await fs.ensureDir(path.dirname(targetFilePath));
        await fs.writeFile(targetFilePath, file.content);
      }

      console.log(`Successfully restored backup ${backup.id}`);
    } catch (error) {
      throw new BackupError(
        `Failed to restore backup ${backup.id}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'restore',
        backup.id
      );
    }
  }

  async list(projectPath: string): Promise<BackupData[]> {
    const backupsDir = path.join(projectPath, this.backupDir);
    
    if (!(await fs.pathExists(backupsDir))) {
      return [];
    }

    try {
      const backupDirs = await fs.readdir(backupsDir, { withFileTypes: true });
      const backups: BackupData[] = [];

      for (const dir of backupDirs) {
        if (dir.isDirectory()) {
          const metadataPath = path.join(backupsDir, dir.name, 'backup.json');
          
          if (await fs.pathExists(metadataPath)) {
            try {
              const backup = await fs.readJson(metadataPath);
              backups.push(backup);
            } catch (error) {
              console.warn(`Failed to read backup metadata: ${metadataPath}`);
            }
          }
        }
      }

      // Sort by timestamp (newest first)
      return backups.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    } catch (error) {
      throw new BackupError(
        `Failed to list backups: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'list'
      );
    }
  }

  async delete(backupId: string): Promise<void> {
    try {
      // Find backup by ID across all potential project paths
      // This is a simplified implementation - in practice you'd want to be more specific
      const potentialPaths = ['.', process.cwd()];
      
      for (const projectPath of potentialPaths) {
        const backupPath = path.join(projectPath, this.backupDir, backupId);
        
        if (await fs.pathExists(backupPath)) {
          await fs.remove(backupPath);
          console.log(`Deleted backup ${backupId}`);
          return;
        }
      }
      
      throw new Error(`Backup ${backupId} not found`);
    } catch (error) {
      throw new BackupError(
        `Failed to delete backup ${backupId}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'delete',
        backupId
      );
    }
  }

  async cleanup(projectPath: string, keepCount: number): Promise<void> {
    try {
      const backups = await this.list(projectPath);
      
      if (backups.length <= keepCount) {
        return; // Nothing to clean up
      }

      // Sort by timestamp and keep the newest ones
      const backupsToDelete = backups
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(keepCount);

      for (const backup of backupsToDelete) {
        try {
          // Use the backup's stored path for more reliable deletion
          if (backup.backupPath && await fs.pathExists(backup.backupPath)) {
            await fs.remove(backup.backupPath);
            console.log(`Deleted backup ${backup.id}`);
          } else {
            // Fallback to the old method if backupPath is not available
            await this.delete(backup.id);
          }
        } catch (error) {
          // Log error but continue with other deletions
          console.warn(`Failed to delete backup ${backup.id}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      console.log(`Cleaned up ${backupsToDelete.length} old backups`);
    } catch (error) {
      // Log error but don't throw - cleanup should be graceful
      console.warn(`Cleanup warning: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private generateBackupId(timestamp: Date): string {
    return `backup-${timestamp.toISOString().replace(/[:.]/g, '-')}`;
  }

  private async collectProjectFiles(projectPath: string): Promise<Array<{ path: string; content: string }>> {
    const files: Array<{ path: string; content: string }> = [];
    const excludeDirs = [
      'node_modules',
      '.git',
      'dist',
      'build',
      '.next',
      'coverage',
      '.nyc_output',
      '.dezzy/backups'
    ];
    
    await this.walkDirectory(projectPath, '', files, excludeDirs);
    return files;
  }

  private async walkDirectory(
    basePath: string, 
    relativePath: string, 
    files: Array<{ path: string; content: string }>,
    excludeDirs: string[]
  ): Promise<void> {
    const fullPath = path.join(basePath, relativePath);
    
    if (!(await fs.pathExists(fullPath))) {
      return;
    }

    const stats = await fs.stat(fullPath);
    
    if (stats.isFile()) {
      try {
        const content = await fs.readFile(fullPath, 'utf8');
        files.push({ path: relativePath, content });
      } catch (error) {
        // Skip files that can't be read (binary files, permission issues, etc.)
        console.warn(`Skipping file ${relativePath}: ${error}`);
      }
    } else if (stats.isDirectory()) {
      const dirName = path.basename(relativePath);
      
      if (excludeDirs.includes(dirName)) {
        return; // Skip excluded directories
      }

      const entries = await fs.readdir(fullPath);
      
      for (const entry of entries) {
        const entryPath = relativePath ? path.join(relativePath, entry) : entry;
        await this.walkDirectory(basePath, entryPath, files, excludeDirs);
      }
    }
  }

  private async clearTargetDirectory(targetPath: string): Promise<void> {
    const preserveDirs = ['node_modules', '.git', '.dezzy/backups'];
    
    if (!(await fs.pathExists(targetPath))) {
      return;
    }

    const entries = await fs.readdir(targetPath, { withFileTypes: true });
    
    for (const entry of entries) {
      const entryPath = path.join(targetPath, entry.name);
      
      if (preserveDirs.includes(entry.name)) {
        continue; // Skip preserved directories
      }

      await fs.remove(entryPath);
    }
  }

  private async detectProjectVersion(projectPath: string): Promise<string> {
    try {
      const packageJsonPath = path.join(projectPath, 'package.json');
      if (await fs.pathExists(packageJsonPath)) {
        const packageJson = await fs.readJson(packageJsonPath);
        
        // Try to get dezzy version from dependencies
        const dezzyVersion = 
          packageJson.dependencies?.dezzy ||
          packageJson.devDependencies?.dezzy ||
          packageJson.peerDependencies?.dezzy;
          
        if (dezzyVersion) {
          return dezzyVersion.replace(/^[\^~]/, '');
        }
        
        // Fall back to project version
        return packageJson.version || '1.0.0';
      }
      
      return '1.0.0';
    } catch (error) {
      return '1.0.0';
    }
  }

  // Utility methods for CLI usage
  async getBackupSize(backupId: string, projectPath: string): Promise<number> {
    const backupPath = path.join(projectPath, this.backupDir, backupId);
    
    if (!(await fs.pathExists(backupPath))) {
      return 0;
    }

    return this.getDirectorySize(backupPath);
  }

  private async getDirectorySize(dirPath: string): Promise<number> {
    let totalSize = 0;
    
    const entries = await fs.readdir(dirPath, { withFileTypes: true });
    
    for (const entry of entries) {
      const entryPath = path.join(dirPath, entry.name);
      
      if (entry.isFile()) {
        const stats = await fs.stat(entryPath);
        totalSize += stats.size;
      } else if (entry.isDirectory()) {
        totalSize += await this.getDirectorySize(entryPath);
      }
    }
    
    return totalSize;
  }

  async validateBackup(backup: BackupData): Promise<{ valid: boolean; issues: string[] }> {
    const issues: string[] = [];
    
    try {
      // Check if backup directory exists
      if (!(await fs.pathExists(backup.backupPath))) {
        issues.push('Backup directory does not exist');
      }

      // Check if metadata file exists
      const metadataPath = path.join(backup.backupPath, 'backup.json');
      if (!(await fs.pathExists(metadataPath))) {
        issues.push('Backup metadata file is missing');
      }

      // Check if essential files exist
      const essentialFiles = ['package.json', 'src'];
      for (const file of essentialFiles) {
        const hasFile = backup.files.some(f => f.path === file || f.path.startsWith(file + '/'));
        if (!hasFile) {
          issues.push(`Essential file/directory missing: ${file}`);
        }
      }

      // Check timestamp validity
      if (!(backup.timestamp instanceof Date) || isNaN(backup.timestamp.getTime())) {
        issues.push('Invalid backup timestamp');
      }
    } catch (error) {
      issues.push(`Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    return {
      valid: issues.length === 0,
      issues
    };
  }

  async get(projectPath: string, backupId: string): Promise<BackupData | undefined> {
    const backups = await this.list(projectPath);
    return backups.find(backup => backup.id === backupId);
  }

  // Emergency recovery methods
  async createEmergencyBackup(projectPath: string): Promise<BackupData> {
    // Create a minimal backup with only essential files
    const timestamp = new Date();
    const backupId = `emergency-${this.generateBackupId(timestamp)}`;
    const backupPath = path.join(projectPath, this.backupDir, backupId);

    await fs.ensureDir(backupPath);

    const essentialPaths = [
      'package.json',
      'src',
      'tsconfig.json',
      '.storybook',
      'README.md'
    ];

    const files: Array<{ path: string; content: string }> = [];

    for (const essentialPath of essentialPaths) {
      const fullPath = path.join(projectPath, essentialPath);
      
      if (await fs.pathExists(fullPath)) {
        const stats = await fs.stat(fullPath);
        
        if (stats.isFile()) {
          const content = await fs.readFile(fullPath, 'utf8');
          files.push({ path: essentialPath, content });
        } else if (stats.isDirectory()) {
          await this.walkDirectory(projectPath, essentialPath, files, []);
        }
      }
    }

    const backup: BackupData = {
      id: backupId,
      timestamp,
      projectPath,
      backupPath,
      version: await this.detectProjectVersion(projectPath),
      files,
      metadata: { emergency: true }
    };

    const metadataPath = path.join(backupPath, 'backup.json');
    await fs.writeJson(metadataPath, backup, { spaces: 2 });

    return backup;
  }
}